import { CodeIcon, ExclamationCircleIcon, ExternalLinkIcon } from "@heroicons/react/outline";
import { ChevronRightIcon } from "@heroicons/react/solid";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import ReleaseLabel from "@/components/ReleaseLabel";
import { Skeleton } from "@/components/ui/skeleton";
import { EntityIcon } from "@/components/ui/icons";
import { EntityType } from "@/queries";
import { useSnippetUsage } from "@/queries/prompts/useSnippetUsage";

interface VersionListProps {
  versions: number[];
  promptRegistryId: number;
  workspaceId: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const VersionList = ({
  versions,
  promptRegistryId,
  workspaceId,
  isExpanded,
  onToggleExpand,
}: VersionListProps) => {
  const sortedVersions = versions.slice().sort((a, b) => b - a);
  const versionsToShow = isExpanded ? sortedVersions : sortedVersions.slice(0, 7);

  return (
    <>
      {versionsToShow.map(version => (
        <Link
          key={version}
          to={`/workspace/${workspaceId}/prompt/${promptRegistryId}/version/${version}`}
          onClick={e => e.stopPropagation()}
          className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
        >
          v{version}
        </Link>
      ))}
      {versions.length > 7 && (
        <button
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onToggleExpand();
          }}
          className="inline-flex items-center px-1 text-xs text-gray-500 hover:text-gray-700 hover:underline"
        >
          {isExpanded ? "Show less" : `Show ${versions.length - 7} more`}
        </button>
      )}
    </>
  );
};

const PromptTemplateSnippets = () => {
  const { promptId, versionId, workspaceId } = useParams();
  const [expandedPrompts, setExpandedPrompts] = useState<Set<number>>(new Set());

  const { data, isLoading, error } = useSnippetUsage({
    promptRegistryId: promptId ? Number(promptId) : undefined,
    promptVersionNumber: versionId ? Number(versionId) : undefined,
  });

  const toggleExpanded = (promptRegistryId: number) => {
    setExpandedPrompts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(promptRegistryId)) {
        newSet.delete(promptRegistryId);
      } else {
        newSet.add(promptRegistryId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full flex-col overflow-auto p-6">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex flex-col items-center text-gray-500">
          <ExclamationCircleIcon className="mb-2 h-8 w-8" />
          <p className="text-sm">Failed to load snippet usage data</p>
        </div>
      </div>
    );
  }

  if (!data || !data.snippet_usage || data.snippet_usage.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-lg text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-gray-300">
            <CodeIcon className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="text-sm font-medium text-gray-600">
            This snippet is not currently referenced by any prompts
          </h3>
          <div className="mt-6 rounded-lg bg-gray-100 p-4">
            <div className="mb-2 text-xs font-semibold text-gray-700">What are snippets?</div>
            <p className="text-xs leading-relaxed text-gray-600">
              Snippets allow you to modularize and reuse pieces of text throughout multiple prompt
              templates. Type{" "}
              <code className="mx-1 rounded bg-white px-1.5 py-0.5 font-mono text-xs">/</code> in
              the prompt editor to insert a snippet.
            </p>
          </div>
          <a
            href="https://docs.promptlayer.com/features/prompt-registry/snippets"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            Learn more about snippets
            <ExternalLinkIcon className="h-3 w-3" />
          </a>
        </div>
      </div>
    );
  }

  // Create a map of prompt_registry_id to label usage items
  const labelUsageMap = new Map<number, string[]>();
  if (data.label_usage) {
    data.label_usage.forEach(labelItem => {
      if (!labelUsageMap.has(labelItem.prompt_registry_id)) {
        labelUsageMap.set(labelItem.prompt_registry_id, []);
      }
      labelUsageMap.get(labelItem.prompt_registry_id)!.push(labelItem.label_name);
    });
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <h3 className="text-sm font-medium text-gray-900">
          {data.total_prompts_using_snippet} prompt
          {data.total_prompts_using_snippet !== 1 ? "s" : ""} reference
          {data.total_prompts_using_snippet === 1 ? "s" : ""} this snippet
        </h3>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-3">
          {data.snippet_usage.map(usage => {
            const highestVersion = Math.max(...usage.version_numbers);
            const labels = labelUsageMap.get(usage.prompt_registry_id) || [];
            return (
              <Link
                key={usage.prompt_registry_id}
                to={`/workspace/${workspaceId}/prompt/${usage.prompt_registry_id}/version/${highestVersion}`}
                className="block rounded-lg border border-gray-200 bg-white p-4 transition-all hover:border-gray-300 hover:shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="flex min-w-0 flex-1 gap-3">
                    <div className="flex-shrink-0">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-50">
                        <EntityIcon type={EntityType.PROMPT} size="sm" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline">
                        {usage.prompt_name}
                      </div>
                      <div className="mt-2">
                        <span className="text-xs text-gray-500">Used in versions: </span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <VersionList
                            versions={usage.version_numbers}
                            promptRegistryId={usage.prompt_registry_id}
                            workspaceId={workspaceId!}
                            isExpanded={expandedPrompts.has(usage.prompt_registry_id)}
                            onToggleExpand={() => toggleExpanded(usage.prompt_registry_id)}
                          />
                        </div>
                      </div>
                      {labels.length > 0 && (
                        <div className="mt-2">
                          <span className="text-xs text-gray-500">Used in labels: </span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {labels.map(labelName => (
                              <ReleaseLabel
                                key={labelName}
                                label={{ id: 0, name: labelName }}
                                showDeleteButton={false}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <ChevronRightIcon className="ml-2 h-4 w-4 flex-shrink-0 text-gray-400" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PromptTemplateSnippets;
